import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  acceptLegalDocuments,
  fetchLegalDocument,
  fetchPendingLegalDocuments,
} from '@/api/legal/legalApi';
import type { LegalDocument, LegalDocumentReference } from '@/api/legal/legalApi.types';
import type { LegalDocumentCode } from '@/constants/legal';
import { LEGAL_DOCUMENT_QUERY_KEY, PENDING_LEGAL_DOCUMENTS_QUERY_KEY } from '@/constants/queryKeys';
import { useAuth } from '@/hooks/useAuth';

export function useLegalDocumentQuery(
  code: LegalDocumentCode | undefined
): UseQueryResult<LegalDocument, Error> {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: [...LEGAL_DOCUMENT_QUERY_KEY, code, i18n.language],
    queryFn: function loadDocument() {
      if (code === undefined) {
        throw new Error('legal document code missing');
      }
      return fetchLegalDocument(code);
    },
    enabled: code !== undefined,
  });
}

export function usePendingLegalDocumentsQuery(): UseQueryResult<LegalDocumentReference[], Error> {
  const { status, activeScopeCode, activeTenantId } = useAuth();
  return useQuery({
    queryKey: [...PENDING_LEGAL_DOCUMENTS_QUERY_KEY, activeScopeCode, activeTenantId],
    queryFn: fetchPendingLegalDocuments,
    enabled:
      status === 'authenticated' && (activeScopeCode === 'tenant' || activeScopeCode === 'client'),
  });
}

export function useAcceptLegalDocumentsMutation(): UseMutationResult<
  void,
  Error,
  LegalDocumentReference[]
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: acceptLegalDocuments,
    onSuccess: function refreshPending() {
      return queryClient.invalidateQueries({ queryKey: PENDING_LEGAL_DOCUMENTS_QUERY_KEY });
    },
  });
}
